// 45-Day Strategy Map derive. Epics are the map; gates are contracts;
// work packages organize effort; founder moves drive today; evidence is progress.
// Never emits task % — continuous epics especially must not look "73% complete".

import { ACTION_OPEN_STATUSES } from './constants';
import { deriveGateReadiness } from './gates';
import { isOutOfScopeAction } from './evidence';
import { diffDays, isoWeekday, weekStart } from './dates';
import {
  orgEpicClass,
  orgEpicMarket,
  orgEpicStrategyStatus,
  orgEpics,
  orgOutcomesForEpic,
  planNeedsSetup,
} from './orgPlan';

const DONE_PACKAGE = new Set(['Done', 'Dropped', 'Complete', 'Completed']);
const EVIDENCE_LEVELS_COUNTED = ['E1', 'E2', 'E3', 'E4', 'E5'];

export function formatEvidenceTally(counts) {
  const parts = EVIDENCE_LEVELS_COUNTED.filter((lvl) => (counts?.[lvl] || 0) > 0).map(
    (lvl) => `${lvl} × ${counts[lvl]}`,
  );
  return parts.length ? parts.join(', ') : 'None';
}

export function isActiveWorkPackage(pkg, nextGateDay) {
  if (!pkg) return false;
  if (DONE_PACKAGE.has(pkg.status)) return false;
  return Number(pkg.gateDay || 15) === Number(nextGateDay);
}

function tallyEvidence(records) {
  const counts = { E1: 0, E2: 0, E3: 0, E4: 0, E5: 0 };
  for (const e of records || []) {
    if (counts[e.level] !== undefined) counts[e.level] += 1;
  }
  return counts;
}

function evidenceForEpic(epicId, evidence, state) {
  const market = orgEpicMarket(state, epicId);
  if (!market) return [];
  return (evidence || []).filter((e) => e.market === market && EVIDENCE_LEVELS_COUNTED.includes(e.level));
}

function openMoves(moves) {
  return (moves || []).filter((m) => ACTION_OPEN_STATUSES.includes(m.status));
}

function rankMoves(moves) {
  const rank = (m) => {
    if (m.status === 'Today' || m.selectedForToday) return 0;
    if (m.status === 'In progress' || m.status === 'In Progress') return 1;
    if (m.status === 'Ready') return 2;
    return 3;
  };
  return [...moves].sort((a, b) => rank(a) - rank(b));
}

function packageCurrentState(pkg, derived, openForPkg) {
  if (pkg.id === 'wp-activate-tiera') {
    const n = derived?.funnel?.counts?.tierA || 0;
    return `${n} active`;
  }
  if (pkg.id === 'wp-universe' || pkg.id === 'wp-import-top50') {
    const n = derived?.funnel?.counts?.universe || 0;
    return `${n} accounts in universe`;
  }
  if (pkg.id === 'wp-convert-discovery') {
    const n = derived?.funnel?.counts?.qualified || 0;
    return `${n} qualified`;
  }
  if (pkg.id === 'wp-dubai-discovery') {
    const n = derived?.funnel?.counts?.discovery || 0;
    return `${n} in discovery`;
  }
  const open = openForPkg.length;
  return `${pkg.status || 'Ready'}; ${open} open move${open === 1 ? '' : 's'}`;
}

function packageConstraint(openForPkg) {
  const blocked = openForPkg.find((m) => m.blocker || m.status === 'Blocked');
  if (blocked) return blocked.blocker || 'Blocked';
  const top = rankMoves(openForPkg)[0];
  return (top?.whyNow || '').trim() || '';
}

function evidenceForPackage(pkg, moves, evidence) {
  const mine = (moves || []).filter((m) => m.workPackageId === pkg.id);
  const ids = new Set(mine.flatMap((m) => m.evidenceRecordIds || []).filter(Boolean));
  const fromIds = (evidence || []).filter((e) => ids.has(e.id));
  if (fromIds.length) return tallyEvidence(fromIds);
  const counts = { E1: 0, E2: 0, E3: 0, E4: 0, E5: 0 };
  for (const m of mine) {
    const lvl = m.evidenceCreated || '';
    if (counts[lvl] !== undefined) counts[lvl] += 1;
  }
  return counts;
}

function riskFromChecks(checks, assessVia) {
  if (!assessVia?.length) return null;
  const subset = (checks || []).filter((c) => assessVia.includes(c.id));
  if (!subset.length) return null;
  const required = subset.filter((c) => !c.optional);
  if (!required.length) return 'Not assessable';
  const unknown = required.filter((c) => c.assessable === false);
  if (unknown.length === required.length) return 'Not assessable';
  if (required.every((c) => c.ok)) return 'Healthy';
  return 'At Risk';
}

function pendingException(state) {
  return (state.exceptions || []).find((e) => !e.decision || e.decision === 'Pending');
}

function governanceRisk(epicId, state, derived) {
  const sprint = derived?.sprint || {};
  const integrity = sprint.integrity || {};
  const today = sprint.today;
  const phase = sprint.phase;
  const day = Number(sprint.day || 0);

  if (epicId === 'E01') {
    const open = openMoves(state.top3);
    const unexcepted = open.some((a) => isOutOfScopeAction(a, state.exceptions));
    if (unexcepted) return 'Violated';
    if (pendingException(state)) return 'At Risk';
    return 'Healthy';
  }

  if (epicId === 'E11') {
    return 'Healthy';
  }

  if (epicId === 'E13') {
    if (integrity.ok === false) return 'Violated';
    if (phase === 'pre') return 'Not assessable';
    const closeouts = state.closeouts || [];
    const recent = closeouts.some((c) => c.date && today && Math.abs(diffDays(c.date, today)) <= 1);
    if (day >= 2 && !recent) return 'At Risk';
    const reviews = state.reviews || [];
    const thisWeek = weekStart(today);
    const hasFridayReview = reviews.some((r) => r.weekStart === thisWeek);
    if (isoWeekday(today) === 5 && !hasFridayReview) return 'At Risk';
    const next = sprint.nextGate;
    if (next && day >= next.day && !(state.gateReviews || {})[next.key]) return 'At Risk';
    return 'Healthy';
  }

  if (epicId === 'E02' || epicId === 'E03') return null;
  return null;
}

function parkedRisk(epicId, state) {
  const open = openMoves(state.top3).filter((m) => m.epicId === epicId);
  if (open.length && !pendingException(state)) return 'Violated';
  return 'Healthy';
}

function deliveryRisk(outcome, checks, evidenceRecords) {
  const fromChecks = riskFromChecks(checks, outcome?.assessVia);
  if (fromChecks) return fromChecks;
  const counts = tallyEvidence(evidenceRecords);
  const total = EVIDENCE_LEVELS_COUNTED.reduce((n, lvl) => n + counts[lvl], 0);
  if (total === 0) return 'At Risk';
  return 'Healthy';
}

function pickNextMove(state, owningPackageIds) {
  const open = rankMoves(openMoves(state.top3));
  const inGap = owningPackageIds.size
    ? open.filter((m) => owningPackageIds.has(m.workPackageId))
    : [];
  const chosen = inGap[0] || open[0] || null;
  if (!chosen) return null;
  return {
    id: chosen.id,
    text: chosen.text,
    workPackageId: chosen.workPackageId || '',
    expectedEvidence: chosen.expectedEvidence || chosen.evidenceCredit || '',
    status: chosen.status,
  };
}

export function derivePlanStrategyMap(state, derived) {
  if (planNeedsSetup(state) && !(state.plan?.epics || []).length) {
    return {
      nextGateDay: Number(derived?.sprint?.nextGate?.day || 15),
      nextGateLabel: derived?.sprint?.nextGate?.label || 'Day 15',
      daysToGate: derived?.sprint?.daysToGate ?? null,
      epics: [],
      progression: {
        gateRequires: 'Complete organization plan setup before the Strategy Map can assess gate risk.',
        missingOutcomes: [],
        owningPackages: [],
        nextMove: null,
      },
      needsSetup: true,
    };
  }

  const nextGateDay = Number(derived?.sprint?.nextGate?.day || 15);
  const gate = deriveGateReadiness(state, derived, nextGateDay);
  const catalog = orgEpics(state);
  const runtimeById = Object.fromEntries((state.plan?.epics || []).map((e) => [e.id, e]));
  const epics = catalog.map((c) => ({ ...c, ...(runtimeById[c.id] || {}) }));
  const packages = state.workPackages || [];
  const moves = state.top3 || [];
  const evidence = state.evidence || [];

  const cards = epics.map((epic) => {
    const epicClass = orgEpicClass(state, epic.id);
    const strategyStatus = orgEpicStrategyStatus(state, epic.id);
    const outcomes = orgOutcomesForEpic(state, epic.id, nextGateDay);
    const primaryOutcome = outcomes[0] || null;
    const requiredOutcome = primaryOutcome
      ? primaryOutcome.requiredOutcome
      : epicClass === 'parked'
        ? 'No work unless a Strategy Exception fires.'
        : 'No contract on this gate.';
    const activePkgs = packages.filter((p) => p.epicId === epic.id && isActiveWorkPackage(p, nextGateDay));
    const epicEvidence = evidenceForEpic(epic.id, evidence, state);
    const evidenceCounts = tallyEvidence(epicEvidence);

    let gateRisk = 'Not assessable';
    if (epicClass === 'parked') {
      gateRisk = parkedRisk(epic.id, state);
    } else if (epicClass === 'continuous') {
      gateRisk = governanceRisk(epic.id, state, derived)
        || riskFromChecks(gate.checks, primaryOutcome?.assessVia)
        || 'Healthy';
    } else if (!primaryOutcome) {
      gateRisk = 'Not assessable';
    } else if (derived?.sprint?.phase === 'pre' && !primaryOutcome?.assessVia?.length) {
      gateRisk = 'Not assessable';
    } else {
      gateRisk = deliveryRisk(primaryOutcome, gate.checks, epicEvidence);
      if (derived?.sprint?.phase === 'pre' && gateRisk === 'At Risk') {
        const fromChecks = riskFromChecks(gate.checks, primaryOutcome?.assessVia);
        if (fromChecks === 'Not assessable') gateRisk = 'Not assessable';
      }
    }

    const activeWorkPackages = activePkgs.map((pkg) => {
      const openForPkg = openMoves(moves).filter((m) => m.workPackageId === pkg.id);
      const pkgCounts = evidenceForPackage(pkg, moves, evidence);
      return {
        id: pkg.id,
        title: pkg.title,
        completionCondition: pkg.completionCondition || '',
        status: pkg.status || 'Ready',
        currentState: packageCurrentState(pkg, derived, openForPkg),
        constraint: packageConstraint(openForPkg),
        openMoveCount: openForPkg.length,
        openMoves: rankMoves(openForPkg).slice(0, 5).map((m) => ({
          id: m.id,
          text: m.text,
          status: m.status,
        })),
        evidenceCounts: pkgCounts,
        evidenceLabel: formatEvidenceTally(pkgCounts),
        gateOutcomeId: pkg.gateOutcomeId || '',
        evidenceCredit: pkg.evidenceCredit || 'none',
      };
    });

    return {
      id: epic.id,
      title: epic.title,
      class: epicClass,
      strategyStatus,
      nextGateDay: primaryOutcome ? nextGateDay : null,
      requiredOutcome,
      gateOutcomeId: primaryOutcome?.id || '',
      activeWorkPackageCount: activeWorkPackages.length,
      evidenceCounts,
      evidenceLabel: formatEvidenceTally(evidenceCounts),
      gateRisk,
      evidenceRequired: epic.evidenceRequired || '',
      acceptance: epic.acceptance || '',
      jiraSpaceKey: epic.jiraSpaceKey || '',
      jiraId: epic.jiraId || '',
      stories: epic.stories || [],
      storyCount: (epic.stories || []).length,
      activeWorkPackages,
    };
  });

  const gapCards = cards.filter((c) => c.gateRisk === 'At Risk' || c.gateRisk === 'Violated' || c.gateRisk === 'Not assessable');
  const missingOutcomes = gapCards
    .filter((c) => c.gateOutcomeId && c.requiredOutcome && c.requiredOutcome !== 'No contract on this gate.')
    .map((c) => ({
      id: c.gateOutcomeId,
      epicId: c.id,
      requiredOutcome: c.requiredOutcome,
      gateRisk: c.gateRisk,
    }));
  const owningPackages = gapCards.flatMap((c) =>
    c.activeWorkPackages.map((p) => ({ id: p.id, title: p.title, epicId: c.id })),
  );
  const owningIds = new Set(owningPackages.map((p) => p.id));
  const nextMove = pickNextMove(state, owningIds);
  const largestGap = gate.compact?.largestGap || missingOutcomes[0]?.requiredOutcome || '';

  return {
    nextGateDay,
    daysToGate: derived?.sprint?.daysToGate ?? null,
    nextGateLabel: derived?.sprint?.nextGate?.label || `Day ${nextGateDay}`,
    gateStatus: gate.compact?.status || '',
    needsSetup: false,
    progression: {
      gateRequires: largestGap || (primaryContract(cards) || 'The next gate has no remaining named gap.'),
      missingOutcomes,
      owningPackages,
      nextMove,
    },
    epics: cards,
  };
}

function primaryContract(cards) {
  const dubai = cards.find((c) => c.id === 'E04');
  return dubai?.requiredOutcome || cards.find((c) => c.gateOutcomeId)?.requiredOutcome || '';
}
